/* eslint-disable max-len */
import { Trans } from '@lingui/macro';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { defaultKnowledgeBaseLinks, gettyKnowledgeBaseLinks } from '@components/kb-article-links';
import { PeopleTaggingBackfillState } from '@components/people_tags/customPeopleTagTypes';
import { isGettyClient } from '@helpers/getty-strings';

// --- Layout wrappers (from Figma frame 386:4373 — 1076x616 parent) ---

const CenteringWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 350px);
`;

interface BannerProps {
  bgColor: string;
}

const Banner = styled.div<BannerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ bgColor }): string => bgColor};
`;

// --- Inner groups ---

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 328px;
`;

const IconWrapper = styled.div`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  position: relative;
`;

const IconCircleBg = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
`;

const IconInner = styled.div`
  position: absolute;
  top: 9px;
  left: 9px;
  width: 24px;
  height: 24px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

interface BadgeProps {
  bgColor: string;
  textColor: string;
}

const Badge = styled.span<BadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: 4px;
  background: ${({ bgColor }): string => bgColor};
  color: ${({ textColor }): string => textColor};
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  white-space: nowrap;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const Heading = styled.h5`
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: #252938;
  margin: 0;
`;

const Description = styled.p`
  font-size: 14px;
  font-weight: 450;
  line-height: 20px;
  color: #353a4a;
  margin: 0;
  width: 328px;
`;

// --- Actions group (from Figma frame 547:9730 — 150x62) ---

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 150px;
`;

const OutlinedButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #8f929c;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  color: #4e5363;
  white-space: nowrap;

  &:hover {
    background: #f5f5f7;
  }
`;

const QuestionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 14px;
`;

const QuestionsText = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  color: #353a4a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TalkToUsText = styled.a`
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  color: #4462c9;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

// --- Reload row (from Figma frame 621:10633 — 83x16) ---

const ReloadRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  color: #353a4a;

  &:hover {
    text-decoration: underline;
  }
`;

// --- SVG icons (exact paths from Figma asset exports) ---

const ScanningIcon: FunctionComponent = () => (
  <IconWrapper>
    <IconCircleBg>
      <svg fill="none" height="42" viewBox="0 0 42 42" width="42">
        <circle cx="21" cy="21" fill="#D0F7EF" r="21" />
      </svg>
    </IconCircleBg>
    <IconInner>
      <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
        <path
          d="M15.5 1.5H22.5V7.33333H20.1667V3.83333H15.5V1.5ZM8.5 1.5V3.83333H3.83333V7.33333H1.5V1.5H8.5ZM15.5 22.5V20.1667H20.1667V16.6667H22.5V22.5H15.5ZM8.5 22.5H1.5V16.6667H3.83333V20.1667H8.5V22.5Z"
          fill="#1C806B"
        />
        <path
          d="M16 17H15V16C15 15.1715 14.3284 14.5 13.5 14.5H10.5C9.67157 14.5 9 15.1715 9 16V17H8V16C8 14.6193 9.11929 13.5 10.5 13.5H13.5C14.8807 13.5 16 14.6193 16 16V17ZM12 12.5C10.3431 12.5 9 11.1568 9 9.5C9 7.84315 10.3431 6.5 12 6.5C13.6568 6.5 15 7.84315 15 9.5C15 11.1568 13.6568 12.5 12 12.5ZM12 11.5C13.1045 11.5 14 10.6046 14 9.5C14 8.39543 13.1045 7.5 12 7.5C10.8954 7.5 10 8.39543 10 9.5C10 10.6046 10.8954 11.5 12 11.5Z"
          fill="#1C806B"
        />
      </svg>
    </IconInner>
  </IconWrapper>
);

const DelayedIcon: FunctionComponent = () => (
  <IconWrapper>
    <svg fill="none" height="42" viewBox="0 0 42 42" width="42">
      <circle cx="21" cy="21" fill="#FCEEB5" r="21" />
      <path
        d="M15 13H13V11H29V13H27V15C27 16.6154 26.1838 17.9147 25.1561 18.9767C24.4532 19.703 23.598 20.372 22.7309 21C23.598 21.628 24.4532 22.297 25.1561 23.0233C26.1838 24.0853 27 25.3846 27 27V29H29V31H13V29H15V27C15 25.3846 15.8162 24.0853 16.8439 23.0233C17.5468 22.297 18.402 21.628 19.2691 21C18.402 20.372 17.5468 19.703 16.8439 18.9767C15.8162 17.9147 15 16.6154 15 15V13ZM17 13V15C17 15.8846 17.4338 16.7103 18.2811 17.5858C19.008 18.337 19.9548 19.0398 21 19.7781C22.0452 19.0398 22.992 18.337 23.7189 17.5858C24.5662 16.7103 25 15.8846 25 15V13H17ZM21 22.2219C19.9548 22.9602 19.008 23.663 18.2811 24.4142C17.4338 25.2897 17 26.1154 17 27V29H25V27C25 26.1154 24.5662 25.2897 23.7189 24.4142C22.992 23.663 22.0452 22.9602 21 22.2219Z"
        fill="#944A12"
      />
    </svg>
  </IconWrapper>
);

const ScheduledIcon: FunctionComponent = () => (
  <IconWrapper>
    <svg fill="none" height="42" viewBox="0 0 42 42" width="42">
      <circle cx="21" cy="21" fill="#D6DEFA" r="21" />
      <path
        d="M21 20C23.7614 20 26 22.2386 26 25V31H24V25C24 23.4023 22.7511 22.0963 21.1763 22.0051L21 22C19.4023 22 18.0963 23.2489 18.0051 24.8237L18 25V31H16V25C16 22.2386 18.2386 20 21 20ZM14.5 23C14.7788 23 15.0501 23.0326 15.3101 23.0942C15.142 23.594 15.0387 24.122 15.009 24.6693L15 25L15.0007 25.0856C14.8876 25.0456 14.7682 25.0187 14.6445 25.0069L14.5 25C13.7203 25 13.0795 25.5949 13.0069 26.3555L13 26.5V31H11V26.5C11 24.567 12.567 23 14.5 23ZM27.5 23C29.433 23 31 24.567 31 26.5V31H29V26.5C29 25.7203 28.4051 25.0796 27.6445 25.0069L27.5 25C27.3248 25 27.1566 25.03 27.0003 25.0852L27 25C27 24.3343 26.8916 23.694 26.6915 23.0956C26.9499 23.0326 27.2211 23 27.5 23ZM14.5 17C15.8807 17 17 18.1193 17 19.5C17 20.8807 15.8807 22 14.5 22C13.1193 22 12 20.8807 12 19.5C12 18.1193 13.1193 17 14.5 17ZM27.5 17C28.8807 17 30 18.1193 30 19.5C30 20.8807 28.8807 22 27.5 22C26.1193 22 25 20.8807 25 19.5C25 18.1193 26.1193 17 27.5 17ZM14.5 19C14.2239 19 14 19.2239 14 19.5C14 19.7761 14.2239 20 14.5 20C14.7761 20 15 19.7761 15 19.5C15 19.2239 14.7761 19 14.5 19ZM27.5 19C27.2239 19 27 19.2239 27 19.5C27 19.7761 27.2239 20 27.5 20C27.7761 20 28 19.7761 28 19.5C28 19.2239 27.7761 19 27.5 19ZM21 11C23.2091 11 25 12.7909 25 15C25 17.2091 23.2091 19 21 19C18.7909 19 17 17.2091 17 15C17 12.7909 18.7909 11 21 11ZM21 13C19.8954 13 19 13.8954 19 15C19 16.1046 19.8954 17 21 17C22.1046 17 23 16.1046 23 15C23 13.8954 22.1046 13 21 13Z"
        fill="#3851A6"
      />
    </svg>
  </IconWrapper>
);

const NoFacesIcon: FunctionComponent = () => (
  <IconWrapper>
    <svg fill="none" height="42" viewBox="0 0 42 42" width="42">
      <circle cx="21" cy="21" fill="#EBECF0" r="21" />
      <g transform="translate(9, 9)">
        <path
          d="M14 14.252V16.3414C13.3744 16.1203 12.7013 16 12 16C8.68629 16 6 18.6863 6 22H4C4 17.5817 7.58172 14 12 14C12.6906 14 13.3608 14.0875 14 14.252ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11ZM19 17.5858L21.1213 15.4645L22.5355 16.8787L20.4142 19L22.5355 21.1213L21.1213 22.5355L19 20.4142L16.8787 22.5355L15.4645 21.1213L17.5858 19L15.4645 16.8787L16.8787 15.4645L19 17.5858Z"
          fill="#4E5363"
        />
      </g>
    </svg>
  </IconWrapper>
);

const ArrowRightIcon: FunctionComponent = () => (
  <svg
    fill="none"
    height="14"
    style={{ flexShrink: 0 }}
    viewBox="0 0 14 14"
    width="14"
  >
    <svg
      height="9.07433"
      viewBox="0 0 9.33333 9.07433"
      width="9.33333"
      x="2.333"
      y="2.463"
    >
      <path
        d="M7.10033 3.95383L3.97133 0.824833L4.79617 0L9.33333 4.53717L4.79617 9.07433L3.97133 8.2495L7.10033 5.1205H0V3.95383H7.10033Z"
        fill="#4462C9"
      />
    </svg>
  </svg>
);

const ReloadIcon: FunctionComponent = () => (
  <svg
    fill="none"
    height="12"
    style={{ flexShrink: 0 }}
    viewBox="0 0 12 12"
    width="12"
  >
    <path
      d="M1 6C1 8.7614 3.23857 11 6 11C8.7614 11 11 8.7614 11 6C11 3.23857 8.7614 1 6 1V2C8.20915 2 10 3.79086 10 6C10 8.20915 8.20915 10 6 10C3.79086 10 2 8.20915 2 6C2 4.62511 2.69367 3.41224 3.75012 2.69226L3.75 4H4.75V1H1.75V2L2.99959 1.99995C1.78537 2.91217 1 4.36436 1 6Z"
      fill="#353A4A"
    />
  </svg>
);

// --- Helpers ---

let knowledgeBaseUrl;
if (isGettyClient()) {
  knowledgeBaseUrl = gettyKnowledgeBaseLinks.peopleTaggingLink;
} else {
  knowledgeBaseUrl = defaultKnowledgeBaseLinks.peopleTaggingLink;
}

function getBrandfolderUrl(): string {
  const slug = BFG.brandfolder_slug;
  return slug ? `/${slug}` : '/';
}

function handleReload(): void {
  window.location.reload();
}

// --- Shared sub-components ---

const BannerActions: FunctionComponent = () => (
  <>
    <ActionsWrapper>
      <OutlinedButton href={getBrandfolderUrl()}>
        <Trans>Go to your Brandfolder</Trans>
      </OutlinedButton>
      <QuestionsRow>
        <QuestionsText>
          <Trans>Have Questions?</Trans>
          {' '}
          <TalkToUsText
            href={knowledgeBaseUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Trans>View help articles</Trans>
          </TalkToUsText>
        </QuestionsText>
        <ArrowRightIcon />
      </QuestionsRow>
    </ActionsWrapper>
    <ReloadRow onClick={handleReload} type="button">
      <ReloadIcon />
      <Trans>Check for updates</Trans>
    </ReloadRow>
  </>
);

// --- Main component ---

interface PeopleTaggingStatusScreenProps {
  uiState: PeopleTaggingBackfillState;
}

export const PeopleTaggingStatusScreen: FunctionComponent<PeopleTaggingStatusScreenProps> = ({
  uiState
}) => {
  switch (uiState) {
    case PeopleTaggingBackfillState.Scanning:
      return (
        <CenteringWrapper>
          <Banner bgColor="#f6fdfc">
            <TopContent>
              <ScanningIcon />
              <BadgeRow>
                <Badge bgColor="#d0f7ef" textColor="#1C806B">
                  <Trans>Scanning</Trans>
                </Badge>
                <TextBlock>
                  <Heading>
                    <Trans>Tagging in the background</Trans>
                  </Heading>
                  <Description>
                    <Trans>
                      We&apos;re scanning your library right now.
                      Faces will appear once the process finishes
                    </Trans>
                  </Description>
                </TextBlock>
              </BadgeRow>
            </TopContent>
            <BannerActions />
          </Banner>
        </CenteringWrapper>
      );

    case PeopleTaggingBackfillState.Delayed:
      return (
        <CenteringWrapper>
          <Banner bgColor="#FDFBF3">
            <TopContent>
              <DelayedIcon />
              <BadgeRow>
                <Badge bgColor="#FCEEB5" textColor="#944A12">
                  <Trans>Scan delayed</Trans>
                </Badge>
                <TextBlock>
                  <Heading>
                    <Trans>We haven&apos;t forgotten you!</Trans>
                  </Heading>
                  <Description>
                    <Trans>
                      Your library is queued up and will start
                      scanning automatically.
                    </Trans>
                  </Description>
                </TextBlock>
              </BadgeRow>
            </TopContent>
            <BannerActions />
          </Banner>
        </CenteringWrapper>
      );

    case PeopleTaggingBackfillState.NotStarted:
    case PeopleTaggingBackfillState.Scheduled:
    default:
      return (
        <CenteringWrapper>
          <Banner bgColor="#f7f9fe">
            <TopContent>
              <ScheduledIcon />
              <BadgeRow>
                <Badge bgColor="#EBECF0" textColor="#4E5363">
                  <Trans>Scan scheduled</Trans>
                </Badge>
                <TextBlock>
                  <Heading>
                    <Trans>People tagging is on</Trans>
                  </Heading>
                  <Description>
                    <Trans>
                      Scanning will start soon. This can take some
                      time depending on your library size.
                    </Trans>
                  </Description>
                </TextBlock>
              </BadgeRow>
            </TopContent>
            <BannerActions />
          </Banner>
        </CenteringWrapper>
      );
  }
};

export const NoFacesEmptyState: FunctionComponent = () => (
  <CenteringWrapper>
    <Banner bgColor="#f7f8fa">
      <TopContent>
        <NoFacesIcon />
        <BadgeRow>
          <Badge bgColor="#d0f7ef" textColor="#1C806B">
            <Trans>Scan complete</Trans>
          </Badge>
          <TextBlock>
            <Heading>
              <Trans>No faces found</Trans>
            </Heading>
            <Description>
              <Trans>
                We scanned your Brandfolder but didn&apos;t find
                any identifiable faces.
              </Trans>
            </Description>
          </TextBlock>
        </BadgeRow>
      </TopContent>
      <BannerActions />
    </Banner>
  </CenteringWrapper>
);
